import { useCallback, useMemo, useState } from "react";
import type {
  TemplateProviderStatusEntity,
  TemplateStructureItemEntity,
  TemplateStructureResponseEntity,
} from "@/lib/backend/api";
import { useMessageTemplateStructure } from "@/lib/query/message-template";

export type Platform = "Whatsapp" | "Boomer" | "SMS";

export interface TemplateItem {
  id: string;
  name: string;
  path: string;
  isFolder: boolean;
  previewText?: string;
  depth: number;
  /** Whether the template is approved for the selected platform (always true for folders) */
  isApprovedForPlatform: boolean;
  /** Provider statuses for this template (null for folders) */
  providerStatuses?: TemplateProviderStatusEntity[] | null;
}

/** Map UI platform to provider type */
const platformToProviderType = (
  platform: Platform,
): TemplateProviderStatusEntity["providerType"] => {
  switch (platform) {
    case "Whatsapp":
      return "META_WHATSAPP";
    case "SMS":
      return "TWILIO";
    case "Boomer":
      return "BOOMER";
  }
};

/** Check if a template is approved for a specific platform */
const isTemplateApprovedForPlatform = (
  item: TemplateStructureItemEntity,
  platform: Platform,
): boolean => {
  // Folders are always "approved"
  if (item.isFolder) {
    return true;
  }

  const providerType = platformToProviderType(platform);
  const providerStatuses = item.providerStatuses;

  if (!providerStatuses || !Array.isArray(providerStatuses)) {
    // No provider statuses means not approved for any platform
    return false;
  }

  const providerStatus = providerStatuses.find((ps) => ps.providerType === providerType);

  return providerStatus?.status === "APPROVED";
};

/** Convert structure item to template item */
const toTemplateItem = (item: TemplateStructureItemEntity, platform: Platform): TemplateItem => ({
  id: item.id,
  name: item.name,
  path: typeof item.path === "string" ? item.path : "",
  isFolder: item.isFolder,
  previewText: typeof item.previewText === "string" ? item.previewText : undefined,
  depth: item.depth,
  isApprovedForPlatform: isTemplateApprovedForPlatform(item, platform),
  providerStatuses: item.providerStatuses,
});

/** Get children of a parent from the structure */
const getChildren = (
  structure: TemplateStructureResponseEntity,
  parentId: string | null,
  platform: Platform,
): TemplateItem[] => {
  const key = parentId ?? "root";
  const childIds = structure.indexes.byParentId[key] ?? [];

  return childIds
    .map((id) => structure.indexes.byId[id])
    .filter((item): item is TemplateStructureItemEntity => !!item)
    .map((item) => toTemplateItem(item, platform))
    .sort((a, b) => {
      // Folders first, then alphabetical
      if (a.isFolder && !b.isFolder) {
        return -1;
      }
      if (!a.isFolder && b.isFolder) {
        return 1;
      }
      return a.name.localeCompare(b.name);
    });
};

/** Find item by name within parent's children */
const findItemByName = (
  structure: TemplateStructureResponseEntity,
  parentId: string | null,
  name: string,
): TemplateStructureItemEntity | null => {
  const key = parentId ?? "root";
  const childIds = structure.indexes.byParentId[key] ?? [];

  for (const id of childIds) {
    const item = structure.indexes.byId[id];
    if (item && item.name.toLowerCase() === name.toLowerCase()) {
      return item;
    }
  }
  return null;
};

interface UseTemplateSelectionOptions {
  /** The currently selected platform to check template approval status */
  platform: Platform;
}

interface UseTemplateSelectionResult {
  /** Current path segments (e.g., ["general", "info"]) */
  currentPath: string[];
  /** Current search query */
  searchQuery: string;
  /** Items at current folder level, filtered by search */
  items: TemplateItem[];
  /** Whether data is loading */
  isLoading: boolean;
  /** Navigate into a folder */
  navigateToFolder: (folderId: string, folderName: string) => void;
  /** Navigate up to parent folder */
  navigateUp: () => void;
  /** Navigate to a specific path index (for breadcrumb navigation) */
  navigateToPathIndex: (index: number) => void;
  /** Set search query */
  setSearchQuery: (query: string) => void;
  /** Reset to root */
  reset: () => void;
  /** Get full template item by ID */
  getTemplateById: (id: string) => TemplateStructureItemEntity | null;
}

/** Hook for template selection with folder navigation */
export function useTemplateSelection({
  platform,
}: UseTemplateSelectionOptions): UseTemplateSelectionResult {
  const { data: structure, isLoading } = useMessageTemplateStructure();

  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [currentParentId, setCurrentParentId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  /** Get items at current folder level, filtered by search */
  const items = useMemo(() => {
    if (!structure) {
      return [];
    }

    const children = getChildren(structure, currentParentId, platform);

    if (!searchQuery.trim()) {
      return children;
    }

    // Filter by search query
    const query = searchQuery.toLowerCase();
    return children.filter(
      (item) =>
        item.name.toLowerCase().includes(query) || item.previewText?.toLowerCase().includes(query),
    );
  }, [structure, currentParentId, searchQuery, platform]);

  /** Navigate into a folder */
  const navigateToFolder = useCallback((folderId: string, folderName: string) => {
    setCurrentParentId(folderId);
    setCurrentPath((prev) => [...prev, folderName]);
    setSearchQuery("");
  }, []);

  /** Navigate up to parent folder */
  const navigateUp = useCallback(() => {
    if (!structure || currentPath.length === 0) {
      return;
    }

    const newPath = currentPath.slice(0, -1);

    // Find parent ID for new path
    let parentId: string | null = null;
    for (const segment of newPath) {
      const item = findItemByName(structure, parentId, segment);
      if (item && item.isFolder) {
        parentId = item.id;
      }
    }

    setCurrentParentId(parentId);
    setCurrentPath(newPath);
    setSearchQuery("");
  }, [structure, currentPath]);

  /** Navigate to a specific path index (for breadcrumb navigation) Index -1 means go to root */
  const navigateToPathIndex = useCallback(
    (index: number) => {
      if (!structure) {
        return;
      }

      if (index < 0) {
        // Go to root
        setCurrentParentId(null);
        setCurrentPath([]);
        setSearchQuery("");
        return;
      }

      const newPath = currentPath.slice(0, index + 1);

      // Find parent ID for new path
      let parentId: string | null = null;
      for (const segment of newPath) {
        const item = findItemByName(structure, parentId, segment);
        if (item && item.isFolder) {
          parentId = item.id;
        }
      }

      setCurrentParentId(parentId);
      setCurrentPath(newPath);
      setSearchQuery("");
    },
    [structure, currentPath],
  );

  /** Reset to root */
  const reset = useCallback(() => {
    setCurrentParentId(null);
    setCurrentPath([]);
    setSearchQuery("");
  }, []);

  /** Get full template item by ID */
  const getTemplateById = useCallback(
    (id: string): TemplateStructureItemEntity | null => {
      if (!structure) {
        return null;
      }
      return structure.indexes.byId[id] ?? null;
    },
    [structure],
  );

  return {
    currentPath,
    searchQuery,
    items,
    isLoading,
    navigateToFolder,
    navigateUp,
    navigateToPathIndex,
    setSearchQuery,
    reset,
    getTemplateById,
  };
}
